import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

const EmailHealthPage = React.lazy(() => import("./EmailHealthPage"));
const AllTenantsHealth = React.lazy(() => import("./AllTenantsHealth"));

/**
 * Comms Health — email deliverability status per tenant. Built 2026-08-11, the
 * day the bloomingbrilliant misroute was found (emailConfig.emailDomain unset →
 * four months of inbound email silently filed to the master tenant).
 *
 * Permission gating follows the platform SSOT pattern: permissions arrays are
 * EMPTY here — permissionLevel is resolved at runtime against the backend
 * manifest (business-builder-backend/src/modules/comms-health/permissions.ts).
 * The all-tenants matrix is additionally gated server-side (TENANT_MASTER_ADMIN
 * on emailHealthAllTenants) — the backend gate is the real boundary.
 */
const commsHealthModuleConfig: ModuleConfig = {
  id: "comms-health",
  name: "Email Health",
  description:
    "End-to-end email deliverability monitoring — loop-back test emails, config checks, 30-day uptime, and a weekly status report.",
  version: "1.0.0",
  enabled: true,
  category: "communication",
  order: 46,
  icon: "🩺",

  // ROUTES - permissionLevel is resolved from backend at runtime
  routes: [
    {
      path: "/email-health",
      component: EmailHealthPage,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/email-health/all-tenants",
      component: AllTenantsHealth,
      permissionLevel: "view",
      useLayout: true,
    },
  ],

  // NAVIGATION - permissionLevel is resolved from backend at runtime.
  // "All Tenants" is deliberately NOT a nav item: this folder is copied
  // verbatim to tenant sites, and a master-only surface must not appear in a
  // tenant's breadcrumb (hide-don't-disable). EmailHealthPage renders the link
  // at runtime only for TENANT_MASTER_ADMIN/MASTER_ADMIN holders; the route
  // stays registered and the backend gate on emailHealthAllTenants is the real
  // boundary.
  navigation: [
    {
      label: "Email Health",
      path: "/email-health",
      icon: "🩺",
      permissionLevel: "view",
    },
  ],

  // Empty arrays = backend manifest is the SSOT (resolved via the
  // modulePermissions query, same as every standards-compliant module).
  permissions: { view: [], create: [], edit: [], delete: [] },

  changelog: [
    {
      version: "1.0.0",
      releaseDate: "2026-08-11",
      title: "Initial release",
      features: [
        "✅ Email system health page: overall verdict, Receiving/Sending/Routing/Accounts status cards",
        "✅ Loop-back test emails (Run test now + rate-limited page-open probe), 30-day uptime, probe history",
        "✅ Weekly status report: toggle, recipients, min test interval, exact-HTML preview",
        "✅ Master-operator all-tenants matrix (worst-first) at /email-health/all-tenants",
      ],
      notes:
        "Built the day the bloomingbrilliant inbound misroute was found — makes that class of silent failure visible.",
    },
  ],
};

export default commsHealthModuleConfig;
